import { MenuItem } from '@affine/component';
import { SidebarCreateButton } from '@affine/core/modules/app-sidebar/views/sidebar-create-button';
import { useI18n } from '@affine/i18n';
import { AddOrganizeIcon, FolderIcon, PageIcon } from '@blocksuite/icons/rc';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { StreamLainMenu } from '../dropdown-panel-chrome/radix-menu';
import { StreamLainMenuSub } from '../dropdown-panel-chrome/radix-menu-sub';

export type SidebarCreateTarget = {
  id: string;
  label: string;
  section: 'personal' | 'teamAreas' | 'apps';
  document?: (name: string) => void;
  folder?: (name: string) => void;
  /** Standalone creation, such as adding a teamspace. */
  create?: (name: string) => void;
};

type RegisterTarget = (target: SidebarCreateTarget) => () => void;
const RegistrationContext = createContext<RegisterTarget | null>(null);
const SECTION_RANK = { personal: 0, teamAreas: 1, apps: 2 };
const TargetsContext = createContext<readonly SidebarCreateTarget[]>([]);

/** Scope creation to this sidebar instance; owners retain all permission checks. */
export function SidebarCreateProvider({ children }: { children: ReactNode }) {
  const [targets, setTargets] = useState<SidebarCreateTarget[]>([]);
  const register = useCallback<RegisterTarget>(target => {
    setTargets(previous => [
      ...previous.filter(item => item.id !== target.id),
      target,
    ]);
    return () =>
      setTargets(previous => previous.filter(item => item !== target));
  }, []);
  return (
    <RegistrationContext.Provider value={register}>
      <TargetsContext.Provider value={targets}>
        {children}
      </TargetsContext.Provider>
    </RegistrationContext.Provider>
  );
}

/** Optional so standalone navigation panels keep their existing local controls. */
export function useSidebarCreateTarget(target: SidebarCreateTarget) {
  const register = useContext(RegistrationContext);
  useLayoutEffect(() => register?.(target), [register, target]);
  return register !== null;
}

export function SidebarSearchCreateMenu({
  name,
  onCreate,
}: {
  name: string;
  onCreate: () => void;
}) {
  const t = useI18n();
  const targets = useContext(TargetsContext);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const ordered = useMemo(
    () =>
      [...targets]
        .filter(target => target.document || target.folder || target.create)
        .sort(
          (a, b) =>
            SECTION_RANK[a.section] - SECTION_RANK[b.section] ||
            Number(!!a.create) - Number(!!b.create) ||
            a.label.localeCompare(b.label)
        ),
    [targets]
  );
  const select = (create?: (name: string) => void) => () => {
    if (!create) return;
    onCreate();
    create(name.trim());
  };
  return (
    <StreamLainMenu
      triggerRef={triggerRef}
      contentOptions={{ side: 'bottom', align: 'end' }}
      items={ordered.map(target =>
        target.create ? (
          <MenuItem
            key={target.id}
            prefixIcon={<AddOrganizeIcon />}
            onSelect={select(target.create)}
          >
            {target.label}
          </MenuItem>
        ) : (
          <StreamLainMenuSub
            key={target.id}
            items={
              <>
                {target.document ? (
                  <MenuItem
                    prefixIcon={<PageIcon />}
                    onSelect={select(target.document)}
                  >
                    {t[
                      'com.streamlain.rootAppSidebar.organize.create.document'
                    ]()}
                  </MenuItem>
                ) : null}
                {target.folder ? (
                  <MenuItem
                    prefixIcon={<FolderIcon />}
                    onSelect={select(target.folder)}
                  >
                    {t[
                      'com.streamlain.rootAppSidebar.organize.create.folder'
                    ]()}
                  </MenuItem>
                ) : null}
              </>
            }
          >
            {target.label}
          </StreamLainMenuSub>
        )
      )}
    >
      <SidebarCreateButton
        ref={triggerRef}
        label={t['Create']()}
        disabled={ordered.length === 0}
        testId="documents-sidebar-create"
      />
    </StreamLainMenu>
  );
}
